import { create } from 'zustand';
import type { FlyerLaunch } from './useCoinFlyer';

/**
 * The bridge that lets a currency mutation launch a coin flyer (`docs/DESIGN.md` §21) without
 * either half knowing about the other.
 *
 * Two constraints collide here, and this module exists solely because of them:
 *
 *  1. **The launch must happen on the SERVER's response, never on the tap** — build-prompt §11.3,
 *     "never optimistic on anything that moves currency." By the time the response lands the shop
 *     sheet has usually closed and the source cell is gone, which is why `useCoinFlyer`'s `launch`
 *     takes a plain `DOMRect` and never an element.
 *  2. **`useCoinFlyer` must be called from a persistent, shell-level component** (its own doc
 *     comment): it removes any still-flying node when the component owning the hook unmounts, so a
 *     hook called from the shop sheet would cut its own flight off the instant the sheet closed.
 *
 * So the rect is read at TAP time, at the tap site, and parked here (`armFlyer`); the mutation's
 * own `onSuccess` — which is the only place that knows the real, server-authoritative delta —
 * calls `requestFlyer(delta)`, which pairs that delta with the parked rect and queues a flight; and
 * `AppShell` (the persistent component that already mounts `ShellsHud`, i.e. the flyer's own
 * destination) drains the queue and calls `launch`.
 *
 * **Why a module-level park rather than threading a ref through the call sites.** Every purchase
 * path in this app already funnels through one `attemptPurchase` function per screen that does NOT
 * receive the originating event (it is re-invoked from `ChallengeSheet` on a second attempt), and
 * both grant paths in `world.ts` are shared by two different screens. Threading a rect from four
 * tap sites, through `mutateAsync`'s single-argument input, into three `onSuccess` handlers would
 * mean widening `PurchaseInput`/the forage and freebie mutation variables with a field the server
 * must then be told to ignore — a UI concern smuggled into a request body. A one-slot park is the
 * same shape `bag/bagCache.ts` already uses for the item-arrival marker, for the same reason: a
 * transient UI cue about *this session's own* action, handed from the code that causes it to the
 * code that draws it, belonging to neither.
 *
 * **Why the park is exactly one slot, not a map keyed by anything.** Every tap site in this app
 * guards against a concurrent second attempt (`pendingItemCode` on both shop screens, `isPending`
 * on the forage and freebie buttons), so there is never more than one in-flight currency mutation
 * per screen, and a second screen's tap could only follow a navigation that unmounted the first.
 * A keyed map would be dead generality. The queue *downstream* of the park is a list, though — a
 * single response can genuinely produce more than one flight if a future caller wants it, and
 * draining a list costs nothing.
 */

/** The tap-time rect, parked between the tap and the server's response. Exactly one slot — see
 *  this module's header comment for why. */
let armed: DOMRect | null = null;

/**
 * Park the tapped element's rect for the mutation that tap is about to start. Takes an element
 * (not a rect) so a call site is one line and cannot forget to read the rect *at tap time* — the
 * whole point is that the read happens now, while the element is still on screen, rather than in
 * the `onSuccess` that runs after it has unmounted. A nullish element disarms instead of throwing:
 * a ref that has not attached yet is a missing beat, never a crash.
 */
export function armFlyer(source: Element | null | undefined): void {
  armed = source ? source.getBoundingClientRect() : null;
}

/** Clear the park without launching. Called from every currency mutation's `onError`, so a failed
 *  purchase's rect can never be picked up hours later by an unrelated grant on another screen. */
export function disarmFlyer(): void {
  armed = null;
}

/** Reads and CLEARS, like `bagCache.takeAcquired` — a parked rect belongs to exactly one flight. */
export function takeArmedRect(): DOMRect | null {
  const rect = armed;
  armed = null;
  return rect;
}

type State = {
  /** Flights queued by `request` and not yet drained by `AppShell`. */
  pending: FlyerLaunch[];
  /** Pair the server's delta with the parked rect and queue a flight. Silently does nothing when
   *  there is no parked rect (an unwired grant path, or a ref that never attached) or the delta is
   *  zero — a purchase in a non-Shells currency moves the Shells balance by 0, and a flyer reading
   *  "−0" travelling to the Shells chip would be a lie about which wallet was touched. */
  request: (delta: number) => void;
  /** Reads and CLEARS. Clearing as it reads is what makes the drain idempotent under
   *  `<StrictMode>`'s double-invoked effects — the second invocation gets an empty list rather than
   *  launching every flight twice. Same contract, and the same reason, as
   *  `bagCache.takeAcquired`. */
  drain: () => FlyerLaunch[];
  reset: () => void;
};

export const useFlyerQueue = create<State>((set, get) => ({
  pending: [],
  request: (delta) => {
    const from = takeArmedRect();
    if (from == null || !Number.isFinite(delta) || delta === 0) return;
    set({ pending: [...get().pending, { from, delta }] });
  },
  drain: () => {
    const out = get().pending;
    if (out.length > 0) set({ pending: [] });
    return out;
  },
  reset: () => { armed = null; set({ pending: [] }); },
}));

/** Callable from outside React — every caller is a react-query `onSuccess`, which is not a hook. */
export const requestFlyer = (delta: number): void => useFlyerQueue.getState().request(delta);
